import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { Zap, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { OracleResult } from "./OracleResult";
import { WeekPicks } from "./WeekPicks";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const BACKDROP = "url('https://images.unsplash.com/photo-1694442735990-f8584f1dc746?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMGNvbnN0ZWxsYXRpb24lMjBkYXJrfGVufDB8fHx8MTc4MjExMDQ2M3ww&ixlib=rb-4.1.0&q=85')";

export function Oracle() {
  const { user } = useAuth();
  const [strategies, setStrategies] = useState([]);
  const [strategy, setStrategy] = useState("ai_oracle");
  const [pick, setPick] = useState(null);
  const [loading, setLoading] = useState(false);
  const generateRef = useRef(null);
  const labelOf = (key) => strategies.find((s) => s.key === key)?.label || key;

  useEffect(() => {
    const h = () => generateRef.current?.();
    window.addEventListener("pb:generate", h);
    return () => window.removeEventListener("pb:generate", h);
  }, []);

  useEffect(() => {
    api.get("/predict/strategies").then((r) => setStrategies(r.data.strategies));
  }, []);

  const generate = async () => {
    setLoading(true);
    setPick(null);
    try {
      const { data } = await api.post("/predict", { strategy });
      setPick(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate pick");
    } finally {
      setLoading(false);
    }
  };
  generateRef.current = generate;

  const savePick = async () => {
    try {
      const { whites, powerball, strategy: strat, confidence, reasoning } = pick;
      await api.post("/picks", { whites, powerball, strategy: strat, confidence, reasoning });
      toast.success(user ? "Pick saved to your history" : "Pick saved to this browser — sign in to keep it across devices");
      window.dispatchEvent(new Event("pb:picks-changed"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  return (
    <section id="oracle" className="relative py-16 sm:py-24 px-4 sm:px-8 lg:px-12">
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-30 mix-blend-screen"
        style={{
          backgroundImage: BACKDROP,
          backgroundSize: "cover",
          backgroundPosition: "center",
          maskImage: "radial-gradient(ellipse at center, black 40%, transparent 75%)",
          WebkitMaskImage: "radial-gradient(ellipse at center, black 40%, transparent 75%)",
        }}
      />
      <div className="relative max-w-4xl mx-auto text-center">
        <div className="label-caps text-oracle">The Oracle Speaks</div>
        <h1 className="font-heading font-light tracking-tighter text-parchment text-5xl sm:text-6xl lg:text-7xl mt-4">
          Ask the numbers.
          <br />
          <span className="italic text-oracle">Receive their answer.</span>
        </h1>
        <p className="font-body text-parchment/60 text-sm sm:text-base mt-6 max-w-2xl mx-auto leading-relaxed">
          The Powerball is cryptographically random — no app can truly predict it. But data has patterns.
          Choose a strategy below and let statistics whisper your numbers.
        </p>

        <div className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-4">
          <Select value={strategy} onValueChange={setStrategy}>
            <SelectTrigger
              data-testid="strategy-select"
              className="w-full sm:w-72 bg-surface border-white/10 text-parchment label-caps focus:ring-oracle"
            >
              <SelectValue placeholder="Choose strategy" />
            </SelectTrigger>
            <SelectContent className="bg-surface border-white/10 text-parchment">
              {strategies.map((s) => (
                <SelectItem key={s.key} value={s.key} data-testid={`strategy-${s.key}`} className="font-body focus:bg-white/5 focus:text-oracle">
                  <span className="label-caps text-parchment">{s.label}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            data-testid="generate-pick-btn"
            onClick={generate}
            disabled={loading}
            className="bg-oracle text-ink hover:bg-[#FFC033] label-caps px-8 py-6 shadow-[0_0_30px_rgba(255,176,0,0.25)] hover:shadow-[0_0_50px_rgba(255,176,0,0.5)] transition-all animate-oracle-pulse"
          >
            {loading ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Divining…</> : <><Zap className="w-4 h-4 mr-2" strokeWidth={1.5} /> Generate Pick</>}
          </Button>
        </div>

        <AnimatePresence mode="wait">
          {pick && (
            <OracleResult key={pick.strategy + JSON.stringify(pick.whites)} pick={pick} label={labelOf(pick.strategy)} onSave={savePick} onRegenerate={generate} />
          )}
        </AnimatePresence>
        <WeekPicks strategy={strategy} strategyLabel={labelOf(strategy)} />
      </div>
    </section>
  );
}
